import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
	useGetMedicalRecordByPatient,
	useGetMedicalRecordSession,
	useGetMedicalRecordSessions,
	useGetPatient,
	useGetSessions,
	useGetUser,
} from '@/services';
import { buildProntuarioPatientContext } from '../utils/mapMedicalRecordToProntuario';
import { buildProntuarioPastSessionListItems } from '../utils/buildProntuarioPastSessionListItems';
import { mapMedicalRecordSessionToPastSessionRecord } from '../utils/mapMedicalRecordSessionToPastSessionRecord';
import {
	isMedicalRecordSessionPostProcessingPending,
	mergeMedicalRecordSessionIntoList,
	resolveOldestSessionMissingListSummary,
} from '@/utils/medicalRecordSessionPostProcessing';
import { cn } from '@/lib/utils';
import { SessionCallProntuarioHeader } from './prontuario/SessionCallProntuarioHeader';
import { SessionCallProntuarioNotesSection } from './prontuario/SessionCallProntuarioNotesSection';
import { SessionCallProntuarioPatientCard } from './prontuario/SessionCallProntuarioPatientCard';
import { SessionCallProntuarioPatientDetailView } from './prontuario/SessionCallProntuarioPatientDetailView';
import { SessionCallProntuarioPastSessionDetailView } from './prontuario/SessionCallProntuarioPastSessionDetailView';
import { SessionCallProntuarioSessionsListView } from './prontuario/SessionCallProntuarioSessionsListView';
import {
	SessionCallProntuarioViewTransition,
	type SessionCallProntuarioView,
} from './prontuario/SessionCallProntuarioViewTransition';
import {
	PRONTUARIO_BG_CLASS,
	PRONTUARIO_NOTES_PADDING_X,
	PRONTUARIO_PANEL_PADDING_X,
	PRONTUARIO_VIEW_TRANSITION_MS,
} from '../constants/prontuarioTheme';
import {
	resolveProntuarioPatientHeader,
	resolveSessionParticipantTarget,
} from '../utils/resolveProntuarioPatientHeader';
import { TUTORIAL_DEMO_QUERY_OPTIONS } from '@/pages/professional/tutorial-demonstracao/tutorialDemoQueryOptions';
import { SessionCallTranscriptionRevealPanel } from './prontuario/SessionCallTranscriptionRevealPanel';
import {
	countTranscriptionChatWords,
	type SessionCallTranscriptionChatMessage,
} from '@/components/session-call/sessionCallTranscriptionChat';

type ProntuarioView = SessionCallProntuarioView;

const MEDICAL_RECORD_SESSIONS_PAGE_SIZE = 20;

type SessionCallProntuarioPanelProps = {
	session?: Sessions.Session | null;
	className?: string;
	onMinimize?: () => void;
	showHeaderRecordingHint?: boolean;
	recordingHintMessage?: string;
	recordingHintKey?: number;
	transcriptionActive?: boolean;
	isProntuarioLiveCaptureEnabled?: boolean;
	prontuarioTranscriptionStoppedNotice?: string | null;
	onDismissHeaderRecordingHint?: () => void;
	transcriptionMessages?: SessionCallTranscriptionChatMessage[];
	onClearProntuarioTranscription?: () => void;
	onStopProntuarioLiveCapture?: () => void;
	onResumeProntuarioLiveCapture?: () => void;
	/** Demo/tutorial: usa cache mock sem refetch na API. */
	demoMode?: boolean;
	/** Oculta painel e controles de transcrição ao vivo no prontuário (ex.: sessão só transcrição). */
	hideInlineTranscription?: boolean;
	onDemoPatientCardOpen?: () => void;
	onDemoSessionsOpen?: () => void;
	onDemoMinimize?: () => void;
	onDemoViewChange?: (view: ProntuarioView) => void;
};

const SessionCallProntuarioPanel = ({
	session,
	className,
	onMinimize,
	showHeaderRecordingHint = false,
	recordingHintMessage,
	recordingHintKey,
	transcriptionActive = true,
	isProntuarioLiveCaptureEnabled = true,
	prontuarioTranscriptionStoppedNotice = null,
	onDismissHeaderRecordingHint,
	transcriptionMessages = [],
	onClearProntuarioTranscription,
	onStopProntuarioLiveCapture,
	onResumeProntuarioLiveCapture,
	demoMode = false,
	hideInlineTranscription = false,
	onDemoPatientCardOpen,
	onDemoSessionsOpen,
	onDemoMinimize,
	onDemoViewChange,
}: SessionCallProntuarioPanelProps) => {
	const queryClient = useQueryClient();
	const [view, setView] = useState<ProntuarioView>('notes');
	const [isTranscriptionOpen, setIsTranscriptionOpen] = useState(false);
	const revealedWordCountRef = useRef<Map<string, number>>(new Map());
	const wasTranscriptionOpenRef = useRef(false);

	if (isTranscriptionOpen && !wasTranscriptionOpenRef.current) {
		const snap = new Map<string, number>();
		for (const message of transcriptionMessages) {
			snap.set(message.id, countTranscriptionChatWords(message.text));
		}
		revealedWordCountRef.current = snap;
	}
	wasTranscriptionOpenRef.current = isTranscriptionOpen;

	const getBaselineWordCount = useCallback(
		(messageId: string) => revealedWordCountRef.current.get(messageId) ?? 0,
		[],
	);

	useEffect(() => {
		if (!demoMode) return;
		onDemoViewChange?.(view);
	}, [demoMode, onDemoViewChange, view]);
	const [selectedPastSessionId, setSelectedPastSessionId] = useState<
		string | null
	>(null);
	const [sessionsListPage, setSessionsListPage] = useState(1);
	const [accumulatedRecordSessions, setAccumulatedRecordSessions] = useState<
		MedicalRecords.MedicalRecordSession[]
	>([]);
	const wasSessionDetailProcessingRef = useRef(false);
	const pastSessionResetTimeoutRef = useRef<number | null>(null);

	const { patientId, userId } = useMemo(
		() => resolveSessionParticipantTarget(session),
		[session],
	);

	const isPatient = !!patientId;

	const demoQueryOptions = demoMode ? TUTORIAL_DEMO_QUERY_OPTIONS : {};

	const {
		data: patient,
		isLoading: isPatientLoading,
		isError: isPatientError,
	} = useGetPatient(patientId ?? '', {
		enabled: isPatient,
		...demoQueryOptions,
	});

	const {
		data: user,
		isLoading: isUserLoading,
		isError: isUserError,
	} = useGetUser(
		{ id: userId ?? '' },
		{
			enabled: !isPatient && !!userId,
			...demoQueryOptions,
		},
	);

	const {
		data: medicalRecord,
		isLoading: isMedicalRecordLoading,
		isError: isMedicalRecordError,
	} = useGetMedicalRecordByPatient(patientId ?? '', {
		enabled: isPatient,
		...demoQueryOptions,
	});

	const resolvedMedicalRecord = isMedicalRecordError
		? null
		: medicalRecord ?? null;

	const medicalRecordSessionParams = useMemo(
		() => ({
			page: sessionsListPage,
			limit: MEDICAL_RECORD_SESSIONS_PAGE_SIZE,
		}),
		[sessionsListPage],
	);

	const {
		data: medicalRecordSessions,
		isLoading: isMedicalRecordSessionsLoading,
		isFetching: isMedicalRecordSessionsFetching,
	} = useGetMedicalRecordSessions(
		resolvedMedicalRecord?.id ?? '',
		medicalRecordSessionParams,
		{
			enabled: !!resolvedMedicalRecord?.id,
			...demoQueryOptions,
		},
	);

	useEffect(() => {
		setSessionsListPage(1);
		setAccumulatedRecordSessions([]);
	}, [resolvedMedicalRecord?.id]);

	useEffect(() => {
		const pageData = medicalRecordSessions?.data;
		if (!pageData) return;

		setAccumulatedRecordSessions((previous) => {
			if (sessionsListPage === 1) {
				if (previous.length === 0) return pageData;
				return previous;
			}

			const existingIds = new Set(
				previous.map((recordSession) => recordSession.id),
			);
			const newSessions = pageData.filter(
				(recordSession) => !existingIds.has(recordSession.id),
			);
			if (newSessions.length === 0) return previous;

			return [...previous, ...newSessions];
		});
	}, [medicalRecordSessions?.data, sessionsListPage]);

	const platformSessionsParams = useMemo(
		() => ({
			isMe: true as const,
			patient_id: patientId ?? undefined,
			page: 1,
			limit: Math.max(
				50,
				sessionsListPage * MEDICAL_RECORD_SESSIONS_PAGE_SIZE,
			),
		}),
		[patientId, sessionsListPage],
	);

	const {
		data: platformSessionsResponse,
		isLoading: isPlatformSessionsLoading,
	} = useGetSessions(platformSessionsParams, {
		enabled:
			isPatient &&
			!!patientId &&
			(view === 'sessions' || view === 'session-detail'),
		...demoQueryOptions,
	});

	const {
		data: selectedPastSessionDetail,
		isLoading: isSelectedPastSessionLoading,
		isError: isSelectedPastSessionError,
	} = useGetMedicalRecordSession(
		resolvedMedicalRecord?.id ?? '',
		selectedPastSessionId ?? '',
		{
			enabled:
				view === 'session-detail' &&
				!!resolvedMedicalRecord?.id &&
				!!selectedPastSessionId,
			pollMode: 'post-process',
			...demoQueryOptions,
		},
	);

	const oldestMissingSummarySession = useMemo(
		() =>
			resolveOldestSessionMissingListSummary(
				accumulatedRecordSessions,
				session?.id,
			),
		[accumulatedRecordSessions, session?.id],
	);

	const { data: listPollSessionData } = useGetMedicalRecordSession(
		resolvedMedicalRecord?.id ?? '',
		oldestMissingSummarySession?.id ?? '',
		{
			enabled:
				view === 'sessions' &&
				!!resolvedMedicalRecord?.id &&
				!!oldestMissingSummarySession?.id,
			pollMode: 'until-summary',
			...demoQueryOptions,
		},
	);

	useEffect(() => {
		if (!listPollSessionData || !resolvedMedicalRecord?.id) return;

		setAccumulatedRecordSessions((previous) =>
			mergeMedicalRecordSessionIntoList(previous, listPollSessionData),
		);

		queryClient.setQueriesData<MedicalRecords.GetMedicalRecordSessionsResponse>(
			{ queryKey: ['medical-record-sessions', resolvedMedicalRecord.id] },
			(cached) => {
				if (!cached) return cached;

				const mergedData = mergeMedicalRecordSessionIntoList(
					cached.data,
					listPollSessionData,
				);
				if (mergedData === cached.data) return cached;

				return {
					...cached,
					data: mergedData,
				};
			},
		);
	}, [listPollSessionData, queryClient, resolvedMedicalRecord?.id]);

	useEffect(() => {
		wasSessionDetailProcessingRef.current = false;
	}, [selectedPastSessionId]);

	useEffect(() => {
		if (!selectedPastSessionDetail || !resolvedMedicalRecord?.id) return;

		const status = selectedPastSessionDetail.pos_process_status;
		const isPending = isMedicalRecordSessionPostProcessingPending(status);

		if (isPending) {
			wasSessionDetailProcessingRef.current = true;
			return;
		}

		if (!wasSessionDetailProcessingRef.current) return;
		wasSessionDetailProcessingRef.current = false;

		setAccumulatedRecordSessions((previous) =>
			mergeMedicalRecordSessionIntoList(
				previous,
				selectedPastSessionDetail,
			),
		);

		queryClient.setQueriesData<MedicalRecords.GetMedicalRecordSessionsResponse>(
			{ queryKey: ['medical-record-sessions', resolvedMedicalRecord.id] },
			(cached) => {
				if (!cached) return cached;

				const mergedData = mergeMedicalRecordSessionIntoList(
					cached.data,
					selectedPastSessionDetail,
				);
				if (mergedData === cached.data) return cached;

				return {
					...cached,
					data: mergedData,
				};
			},
		);
	}, [queryClient, resolvedMedicalRecord?.id, selectedPastSessionDetail]);

	const selectedPastSession = useMemo(() => {
		if (!selectedPastSessionDetail) return null;

		const platformSession = platformSessionsResponse?.data?.find(
			(platformSession) =>
				platformSession.id === selectedPastSessionDetail.session_id,
		);

		return mapMedicalRecordSessionToPastSessionRecord(
			selectedPastSessionDetail,
			platformSession?.selected_date,
		);
	}, [platformSessionsResponse?.data, selectedPastSessionDetail]);

	const pastSessions = useMemo(
		() =>
			buildProntuarioPastSessionListItems({
				recordSessions: accumulatedRecordSessions,
				platformSessions: platformSessionsResponse?.data,
				currentPlatformSessionId: session?.id,
			}),
		[
			accumulatedRecordSessions,
			platformSessionsResponse?.data,
			session?.id,
		],
	);

	const hasMorePastSessions =
		(medicalRecordSessions?.pagination.total ?? 0) >
		accumulatedRecordSessions.length;

	const isLoadingMorePastSessions =
		isMedicalRecordSessionsFetching && sessionsListPage > 1;

	const handleLoadMorePastSessions = () => {
		if (!hasMorePastSessions || isLoadingMorePastSessions) return;
		setSessionsListPage((page) => page + 1);
	};

	const isSessionsListLoading =
		(!!resolvedMedicalRecord?.id &&
			isMedicalRecordSessionsLoading &&
			sessionsListPage === 1) ||
		(view === 'sessions' &&
			isPlatformSessionsLoading &&
			sessionsListPage === 1);

	const patientContext = useMemo(
		() => buildProntuarioPatientContext(resolvedMedicalRecord, patient),
		[resolvedMedicalRecord, patient],
	);

	const isProfileLoading = isPatient
		? isPatientLoading || (isMedicalRecordLoading && !isMedicalRecordError)
		: isUserLoading;

	const isProfileError = isPatient ? isPatientError : isUserError;

	const patientHeader = useMemo(
		() =>
			resolveProntuarioPatientHeader(
				session,
				patient,
				user,
				patientContext,
				accumulatedRecordSessions,
			),
		[accumulatedRecordSessions, patient, patientContext, session, user],
	);

	const selectedPastSessionListItem = useMemo(() => {
		if (!selectedPastSessionId) return undefined;

		const fromPastList = pastSessions.find(
			(record) => record.id === selectedPastSessionId,
		);
		if (fromPastList) return fromPastList;

		const recordSession = accumulatedRecordSessions.find(
			(record) => record.id === selectedPastSessionId,
		);
		if (!recordSession) return undefined;

		const platformSession = platformSessionsResponse?.data?.find(
			(platformSession) =>
				platformSession.id === recordSession.session_id,
		);

		return mapMedicalRecordSessionToPastSessionRecord(
			recordSession,
			platformSession?.selected_date,
		);
	}, [
		accumulatedRecordSessions,
		pastSessions,
		platformSessionsResponse?.data,
		selectedPastSessionId,
	]);

	const detailRecord = selectedPastSession ?? selectedPastSessionListItem;

	useEffect(() => {
		return () => {
			if (pastSessionResetTimeoutRef.current !== null) {
				window.clearTimeout(pastSessionResetTimeoutRef.current);
			}
		};
	}, []);

	const handleBack = () => {
		if (view === 'session-detail') {
			setView('sessions');
			if (pastSessionResetTimeoutRef.current !== null) {
				window.clearTimeout(pastSessionResetTimeoutRef.current);
			}
			pastSessionResetTimeoutRef.current = window.setTimeout(() => {
				pastSessionResetTimeoutRef.current = null;
				setSelectedPastSessionId(null);
			}, PRONTUARIO_VIEW_TRANSITION_MS);
			return;
		}

		setView('notes');
	};

	const handleCloseTranscription = () => {
		setIsTranscriptionOpen(false);
	};

	const handleToggleTranscriptionHistory = () => {
		onDismissHeaderRecordingHint?.();
		setIsTranscriptionOpen((open) => !open);
	};

	const handleStopProntuarioLiveCapture = () => {
		onStopProntuarioLiveCapture?.();
		setIsTranscriptionOpen(true);
	};

	const handleHeaderBack = () => {
		handleBack();
	};

	const handleOpenPastSession = (sessionId: string) => {
		setSelectedPastSessionId(sessionId);
		setView('session-detail');
	};

	const headerLeftAction =
		view === 'notes'
			? 'sessions-shortcut'
			: view === 'sessions'
			  ? 'notes-back'
			  : view === 'session-detail'
			    ? 'sessions-back'
			    : 'notes-back';

	const renderProntuarioView = (activeView: ProntuarioView) => {
		switch (activeView) {
			case 'notes':
				return (
					<div className="daily-scrollbar flex h-full min-h-0 flex-col overflow-y-auto overflow-x-hidden">
						<div className={cn(PRONTUARIO_NOTES_PADDING_X, 'pt-3')}>
							<SessionCallProntuarioPatientCard
								header={patientHeader}
								onOpenProfile={() => {
									if (demoMode) onDemoPatientCardOpen?.();
									setView('patient-detail');
								}}
								{...(demoMode
									? {
											tutorialDemoTarget:
												'prontuario-patient-card',
									  }
									: {})}
							/>
						</div>
						<SessionCallProntuarioNotesSection
							session={session}
							className="min-h-0 flex-1"
							{...(demoMode
								? { tutorialDemoTarget: 'prontuario-notes' }
								: {})}
						/>
					</div>
				);

			case 'patient-detail':
				return (
					<div
						className="h-full min-h-0 flex-1"
						{...(demoMode
							? {
									'data-tutorial-demo':
										'prontuario-patient-detail',
							  }
							: {})}
					>
						<SessionCallProntuarioPatientDetailView
							className="h-full min-h-0 flex-1"
							header={patientHeader}
							session={session}
							patient={patient}
							user={user}
							clinicalHistory={patientContext.clinicalHistory}
							therapeuticPlan={patientContext.therapeuticPlan}
							isLoading={isProfileLoading}
							isError={isProfileError}
						/>
					</div>
				);

			case 'sessions':
				return (
					<div
						className="daily-scrollbar h-full min-h-0 overflow-y-auto overflow-x-hidden"
						{...(demoMode
							? {
									'data-tutorial-demo':
										'prontuario-sessions-list',
							  }
							: {})}
					>
						<div className={cn(PRONTUARIO_PANEL_PADDING_X, 'py-3')}>
							<SessionCallProntuarioSessionsListView
								currentSession={session}
								recordSessions={accumulatedRecordSessions}
								platformSessions={
									platformSessionsResponse?.data
								}
								isLoading={isSessionsListLoading}
								hasMorePastSessions={hasMorePastSessions}
								isLoadingMorePastSessions={
									isLoadingMorePastSessions
								}
								onLoadMorePastSessions={
									handleLoadMorePastSessions
								}
								onOpenCurrentSession={() => setView('notes')}
								onOpenPastSession={handleOpenPastSession}
							/>
						</div>
					</div>
				);

			case 'session-detail':
				return selectedPastSessionId && detailRecord ? (
					<div className="daily-scrollbar h-full min-h-0 overflow-y-auto overflow-x-hidden">
						<div className={cn(PRONTUARIO_PANEL_PADDING_X, 'py-3')}>
							<SessionCallProntuarioPastSessionDetailView
								record={detailRecord}
								isLoading={isSelectedPastSessionLoading}
								isError={isSelectedPastSessionError}
								postProcessStatus={
									selectedPastSessionDetail?.pos_process_status
								}
							/>
						</div>
					</div>
				) : null;

			default:
				return null;
		}
	};

	const handleMinimize = () => {
		if (demoMode) onDemoMinimize?.();
		onMinimize?.();
	};

	return (
		<aside
			data-prontuario-panel
			{...(demoMode
				? { 'data-tutorial-demo': 'prontuario-content' }
				: {})}
			className={cn(
				'relative z-10 flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden',
				PRONTUARIO_BG_CLASS,
				className,
			)}
		>
			<SessionCallProntuarioHeader
				leftAction={headerLeftAction}
				onBack={handleHeaderBack}
				onOpenSessions={() => {
					if (demoMode) onDemoSessionsOpen?.();
					setView('sessions');
				}}
				onMinimize={handleMinimize}
				showRecordingHint={showHeaderRecordingHint}
				recordingHintMessage={recordingHintMessage}
				recordingHintKey={recordingHintKey}
				transcriptionActive={transcriptionActive}
				isProntuarioLiveCaptureEnabled={isProntuarioLiveCaptureEnabled}
				onDismissRecordingHint={onDismissHeaderRecordingHint}
				{...(hideInlineTranscription
					? { hideInlineTranscription: true }
					: {
							onOpenTranscriptionHistory:
								handleToggleTranscriptionHistory,
							onClearTranscriptionConversation:
								onClearProntuarioTranscription,
							onStopProntuarioLiveCapture:
								handleStopProntuarioLiveCapture,
							onResumeProntuarioLiveCapture:
								onResumeProntuarioLiveCapture,
							isTranscriptionHistoryOpen: isTranscriptionOpen,
							isLeftActionDisabled: isTranscriptionOpen,
					  })}
				{...(demoMode
					? {
							tutorialDemoTarget: 'prontuario-header',
							tutorialDemoSessionsOpen:
								'prontuario-sessions-open',
							tutorialDemoMinimize: 'prontuario-minimize',
					  }
					: {})}
			/>

			<div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
				<SessionCallProntuarioViewTransition
					view={view}
					renderView={renderProntuarioView}
				/>

				{!hideInlineTranscription ? (
					<SessionCallTranscriptionRevealPanel
						isOpen={isTranscriptionOpen}
						messages={transcriptionMessages}
						transcriptionActive={transcriptionActive}
						isProntuarioLiveCaptureEnabled={
							isProntuarioLiveCaptureEnabled
						}
						stoppedNotice={prontuarioTranscriptionStoppedNotice}
						getBaselineWordCount={getBaselineWordCount}
						onClose={handleCloseTranscription}
					/>
				) : null}
			</div>
		</aside>
	);
};

export { SessionCallProntuarioPanel };
